// @ts-check
import { useState } from 'react';
import { useToast } from '../../context/ToastContext';
import adminWholesaleService from '../../services/adminWholesaleService';
import { WHOLESALE_CHANNEL } from '../../utils/wholesale';
import Modal from '../ui/Modal';
import { EnumSelect, Field, input, PhoneField, primaryBtn, secondaryBtn } from './shared';

/** @param {{partnerId: string, contact?: any, onClose: () => void, onSaved: () => void}} props */
const ContactModal = ({ partnerId, contact, onClose, onSaved }) => {
  const toast = useToast();
  const [form, setForm] = useState({
    name: contact?.name || '',
    role: contact?.role || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    whatsapp: contact?.whatsapp || '',
    instagram: contact?.instagram || '',
    is_decision_maker: contact?.is_decision_maker || false,
    preferred_channel: contact?.preferred_channel || 'email',
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    if (!form.email.trim() && !form.phone && !form.whatsapp && !form.instagram.trim()) {
      return toast.error('Add at least one way to reach this contact: email, phone, WhatsApp or Instagram');
    }
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, typeof v === 'string' && !v.trim() ? null : v]));
    setSaving(true);
    try {
      if (contact) await adminWholesaleService.updateContact(contact.id, body);
      else await adminWholesaleService.addContact(partnerId, body);
      toast.success(contact ? 'Contact updated' : 'Contact added');
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Failed to save the contact');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal maxWidth="md" onClose={onClose}>
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold">{contact ? 'Edit contact' : 'Add contact'}</h2>
      </div>
      <div className="px-6 py-4 space-y-3 max-h-[65vh] overflow-y-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Name">
            <input className={input} value={form.name} onChange={(e) => set({ name: e.target.value })} />
          </Field>
          <Field label="Role" hint="Buyer, founder, merchandiser">
            <input className={input} value={form.role} onChange={(e) => set({ role: e.target.value })} />
          </Field>
        </div>
        <Field label="Email">
          <input className={input} type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
        </Field>
        <Field label="Phone">
          <PhoneField ariaLabel="Phone" placeholder="Phone number" value={form.phone} onChange={(phone) => set({ phone })} />
        </Field>
        <Field label="WhatsApp">
          <PhoneField ariaLabel="WhatsApp" placeholder="WhatsApp number" value={form.whatsapp} onChange={(whatsapp) => set({ whatsapp })} />
        </Field>
        <Field label="Instagram">
          <input className={input} placeholder="@handle" value={form.instagram} onChange={(e) => set({ instagram: e.target.value })} />
        </Field>
        <Field label="Preferred channel" hint="The channel reminders name for this door">
          <EnumSelect value={form.preferred_channel} onChange={(preferred_channel) => set({ preferred_channel })} options={Object.values(WHOLESALE_CHANNEL)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_decision_maker} onChange={(e) => set({ is_decision_maker: e.target.checked })} />
          Decides yes or no
        </label>
      </div>
      <div className="flex gap-2 px-6 py-4 border-t">
        <button onClick={save} disabled={saving} className={primaryBtn}>
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button onClick={onClose} disabled={saving} className={secondaryBtn}>
          Cancel
        </button>
      </div>
    </Modal>
  );
};

export default ContactModal;
